import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  input,
  Input,
  Output,
  signal,
  TemplateRef,
  inject
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, QueryParamsHandling, Router } from '@angular/router';
import { PermissionsService } from '@coin/shared/auth/feature';
import { HeaderPortalService } from '@coin/shared/core/feature-state';
import { V2TabDirective, RoutedTabsComponent } from '@coin/shared/core/ui-storybook';
import { Admin, RouteBuilder, RouteTree } from '@coin/shared/core/util-helpers';
import { CoinTab } from '@coin/shared/core/util-models';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { filter, startWith } from 'rxjs/operators';
import { MatIcon } from '@angular/material/icon';
import { NgTemplateOutlet } from '@angular/common';
import { ViewInformationComponent } from '../view-information/view-information.component';

@Component({
  selector: 'admin-page-headline',
  templateUrl: './page-headline.component.html',
  styleUrls: ['./page-headline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, RoutedTabsComponent, V2TabDirective, NgTemplateOutlet, ViewInformationComponent, TranslatePipe]
})
export class PageHeadlineComponent {
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);
  protected headerPortalService = inject(HeaderPortalService);

  translationPrefix = input<string>();
  breadcrumbTitle = input<string | { [param: string]: string }>();
  @Input() tabs: CoinTab[] = [];
  defaultToFirstTab = input(false);
  headline = input<string>();
  showBackButton = input(true);
  rightTemplate = input<TemplateRef<unknown>>();
  showInformation = input(true);
  preNavigateBack = input<(url: string) => Observable<string>>();
  queryParamsHandling = input<QueryParamsHandling>('');
  hideTab = input<(tab: CoinTab) => boolean>(() => false);

  @Output() tabChanged?: EventEmitter<V2TabDirective> = new EventEmitter();

  private readonly url = signal<URL>(undefined);
  private readonly breadCrumbs = computed(() => Admin.getBreadcrumbs(this.url()));
  protected breadcrumbTexts = computed(() => {
    return this.breadCrumbs().flatMap(breadcrumb => {
      const translatedTitle = breadcrumb.title ? this.translateService.instant(breadcrumb.title) : null;

      if (breadcrumb.params?.includes(breadcrumb.paramTitle)) {
        const breadcrumbTitle = this.getBreadcrumbTitle(this.url(), breadcrumb.paramTitle, this.breadcrumbTitle());
        return translatedTitle ? [breadcrumbTitle, translatedTitle] : [breadcrumbTitle];
      }
      if (!translatedTitle) {
        throw new Error('No title-translation set!');
      }
      return translatedTitle;
    });
  });
  protected backRoute = computed(
    () =>
      this.breadCrumbs()
        .filter(b => !b.emptyPage)
        .reverse()[1]?.url ?? '/'
  );
  private readonly currentRoute = computed(() => this.url()?.pathname);

  protected helpTranslationKeyPrefix = computed(() => `${this.translationPrefix()}.`);

  constructor() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed()
      )
      .subscribe(() => this.updateBreadcrumbsAndTabs());
  }

  private updateBreadcrumbsAndTabs(refreshTabs = false): void {
    if (this.currentRoute() === window.location.pathname) {
      return;
    }

    this.url.set(new URL(window.location.href));
    this.tabs =
      !refreshTabs && this.tabs?.length > 0
        ? this.tabs
        : this.permissionsService.getSubPageTabs(this.url(), Admin as unknown as RouteBuilder<RouteTree>);
    this.tabs.forEach(tab => {
      if ('route' in tab) {
        tab.isSelected = tab.route === this.currentRoute();
      }
    });
  }

  public navigateBack(): void {
    if (this.preNavigateBack()) {
      this.preNavigateBack()(this.backRoute())
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(url => this.router.navigateByUrl(url));
    } else {
      this.router.navigate([this.backRoute()], { queryParamsHandling: this.queryParamsHandling() });
    }
  }

  public async onTabChange(coinTab: V2TabDirective): Promise<void> {
    if (this.tabChanged.observed) {
      this.tabChanged.emit(coinTab);
    } else {
      this.updateBreadcrumbsAndTabs(true);
    }
  }

  private getBreadcrumbTitle(
    url: URL,
    paramTitle: string,
    breadcrumbTitle: string | { [param: string]: string }
  ): string {
    if (!breadcrumbTitle) return url.searchParams.get(paramTitle);
    if (typeof breadcrumbTitle === 'string') return breadcrumbTitle;
    return breadcrumbTitle[paramTitle];
  }
}
